'use client';
import { useScreenSize } from '@smartproxy-web/shared/hooks';
import React, { useCallback, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import AccordionItem, {
  AccordionItemProps,
} from '@/decodo/components/molecules/accordion/AccordionItem';
import {
  AccordionProvider,
  useAccordion,
} from '@/decodo/components/molecules/accordion/AccordionProvider';

export interface AccordionListProps {
  accordionList: {
    header: React.ReactNode;
    children: React.ReactNode;
  }[];
}

export type AccordionListItemProps = {
  isMultiMode?: boolean;
  showTimerLine?: boolean;
  internal?: {
    ContainerProps?: React.HTMLAttributes<HTMLDivElement>;
    accordionItem?: Partial<AccordionItemProps>;
  };
} & AccordionListProps;

export function AccordionListComponent({
  accordionList,
  internal,
  showTimerLine,
}: AccordionListItemProps) {
  const { accordionItem, ContainerProps } = internal || {};
  const { openItemId, setOpenItemId } = useAccordion();
  const [timerKey, setTimerKey] = useState(0);
  const [hasUserClicked, setHasUserClicked] = useState(false);
  const { isDesktop } = useScreenSize();

  useEffect(() => {
    if (hasUserClicked || !showTimerLine || !isDesktop) return;

    const currentIndex = Number(openItemId ?? 0);
    const nextIndex = (currentIndex + 1) % accordionList.length;

    const timer = setTimeout(() => {
      setOpenItemId(String(nextIndex));
    }, 7000);

    setTimerKey((prev) => prev + 1);

    return () => clearTimeout(timer);
  }, [
    openItemId,
    accordionList.length,
    setOpenItemId,
    hasUserClicked,
    showTimerLine,
    isDesktop,
  ]);

  const handleUserClick = useCallback(
    (id: string) => {
      setHasUserClicked(true);
      setOpenItemId(id);
    },
    [setOpenItemId]
  );

  return (
    <div
      {...ContainerProps}
      data-tracker-component='accordion'
      className={cn(
        'flex w-full flex-col text-left',
        ContainerProps?.className
      )}
    >
      {accordionList?.map((item, index) => {
        const id = String(index);
        const isActive = id === openItemId;

        return (
          <div key={id} className='relative'>
            <div onClick={() => handleUserClick(id)}>
              <AccordionItem {...accordionItem} id={id} header={item.header}>
                {item.children}
              </AccordionItem>

              {isActive && !hasUserClicked && showTimerLine && (
                <div
                  key={`timer-${timerKey}`}
                  className='bg-gradient-secondary-4 absolute bottom-0 left-0 hidden h-[3px] origin-left md:flex'
                  style={{ width: '100%' }}
                />
              )}
            </div>
          </div>
        );
      })}
      <div className='border-b border-solid border-gray-200' />
    </div>
  );
}

export default function AccordionList(
  props: AccordionListItemProps & { isMultiMode?: boolean }
) {
  const { isMultiMode = false, ...rest } = props;
  return (
    <AccordionProvider isMultiMode={isMultiMode}>
      <AccordionListComponent {...rest} />
    </AccordionProvider>
  );
}
